// Deterministic mock adapter. Seeded PRNG keyed on filters so values are stable
// across re-renders. Backs the metrics with no real backend yet (and serves as
// fallback for REST methods not implemented). Replace per-metric via index.ts.

import {
  ByDimensionResponse,
  ByUserResponse,
  CallDetail,
  CallLogRow,
  CallStatsFilters,
  CallStatus,
  CallType,
  CallsPage,
  CallsQuery,
  ConnectivityResponse,
  DimensionBucket,
  FailureReason,
  FailuresResponse,
  IceType,
  MetricSample,
  PairSummary,
  Participant,
  QualityDimension,
  Scores,
  SummaryResponse,
  TimeBucket,
  TimeWindow,
} from '../callStatsTypes';
import { CallStatsDataSource } from './CallStatsDataSource';

const ROSTER: Participant[] = [
  { userId: 'u-1001', displayName: 'Ada Lovelace' },
  { userId: 'u-1002', displayName: 'Grace Hopper' },
  { userId: 'u-1003', displayName: 'Alan Turing' },
  { userId: 'u-1004', displayName: 'Katherine Johnson' },
  { userId: 'u-1005', displayName: 'Linus Torvalds' },
  { userId: 'u-1006', displayName: 'Margaret Hamilton' },
  { userId: 'u-1007', displayName: 'Dennis Ritchie' },
  { userId: 'u-1008', displayName: 'Barbara Liskov' },
  { userId: 'cc-2001', displayName: 'Ōmcare Customer Care' },
];

const WINDOW_HOURS: Record<TimeWindow, number> = {
  '1h': 1,
  '3h': 3,
  '6h': 6,
  '1d': 24,
  '7d': 168,
  custom: 24,
};

function mulberry32(seed: number) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hash(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function seedFor(filters: CallStatsFilters, salt: string): number {
  return hash(
    `${filters.window}|${filters.from ?? ''}|${filters.to ?? ''}|${
      filters.userId ?? ''
    }|${filters.peerUserId ?? ''}|${salt}`,
  );
}

function windowHours(filters: CallStatsFilters): number {
  if (filters.window === 'custom' && filters.from && filters.to) {
    const ms =
      new Date(filters.to).getTime() - new Date(filters.from).getTime();
    return Math.max(1, Math.round(ms / 3_600_000));
  }
  return WINDOW_HOURS[filters.window];
}

const clamp = (n: number, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, n));
const round = (n: number, d = 0) => {
  const f = 10 ** d;
  return Math.round(n * f) / f;
};

function pickStatus(r: () => number): CallStatus {
  const x = r();
  if (x < 0.78) return 'completed';
  if (x < 0.86) return 'no_answer';
  if (x < 0.92) return 'rejected';
  if (x < 0.97) return 'disconnected';
  return 'failed';
}

function pickIce(r: () => number): IceType {
  const x = r();
  if (x < 0.55) return 'host';
  if (x < 0.82) return 'srflx_stun';
  return 'relay_turn';
}

function scoresFor(
  type: CallType,
  status: CallStatus,
  r: () => number,
): {
  scores: Scores;
  mos: number;
  rtt: number;
  loss: number;
  jitter: number;
  bitrate: number;
} {
  const failed = status !== 'completed';
  const mos = failed ? round(1 + r() * 1.2, 2) : round(3.4 + r() * 1.5, 2);
  const loss = failed ? round(4 + r() * 8, 1) : round(r() * 3, 1);
  const jitter = round(8 + r() * (failed ? 60 : 30), 1);
  const rtt = round(40 + r() * (failed ? 320 : 160), 0);
  const bitrate =
    type === 'video' ? round(500 + r() * 1500, 0) : round(48 + r() * 16, 0);
  const audio = clamp(
    round(((mos - 1) / 4) * 100 - 1.5 * loss - 0.2 * Math.max(0, jitter - 30)),
  );
  const video =
    type === 'video'
      ? clamp(
          round(
            100 -
              2 * loss -
              0.15 * Math.max(0, rtt - 150) -
              0.2 * Math.max(0, jitter - 30),
          ),
        )
      : 0;
  const iceP = pickIce(r);
  const icePenalty = iceP === 'host' ? 0 : iceP === 'srflx_stun' ? 5 : 20;
  const connectivity = clamp(
    round(100 - 0.1 * Math.max(0, rtt - 100) - 1.5 * loss - icePenalty),
  );
  const experience =
    type === 'video'
      ? round(0.45 * audio + 0.3 * video + 0.25 * connectivity)
      : round(0.7 * audio + 0.3 * connectivity);
  return {
    scores: { experience, video, audio, mos, connectivity, callsScored: 1 },
    mos,
    rtt,
    loss,
    jitter,
    bitrate,
  };
}

function genCalls(filters: CallStatsFilters, salt = 'calls'): CallLogRow[] {
  const r = mulberry32(seedFor(filters, salt));
  const hours = windowHours(filters);
  const count = Math.max(6, Math.round(hours * (2 + r() * 4)));
  const now = filters.to ? new Date(filters.to).getTime() : Date.now();
  const spanMs = hours * 3_600_000;
  const rows: CallLogRow[] = [];
  for (let i = 0; i < count; i += 1) {
    const type: CallType = r() < 0.68 ? 'video' : 'sip';
    const status = pickStatus(r);
    const a = ROSTER[Math.floor(r() * ROSTER.length)];
    let b = ROSTER[Math.floor(r() * ROSTER.length)];
    if (b.userId === a.userId)
      b = ROSTER[(ROSTER.indexOf(a) + 1) % ROSTER.length];
    const durationSec =
      status === 'completed'
        ? Math.round(20 + r() * 1200)
        : Math.round(r() * 15);
    const q = scoresFor(type, status, r);
    rows.push({
      callId: `call-${hash(`${salt}-${i}-${filters.window}`).toString(16)}`,
      startedAt: new Date(now - r() * spanMs).toISOString(),
      type,
      status,
      durationSec,
      participants: type === 'sip' ? [a] : [a, b],
      experienceScore: q.scores.experience,
      mos: q.mos,
      rttMs: q.rtt,
      packetLossPct: q.loss,
      jitterMs: q.jitter,
      bitrateKbps: q.bitrate,
      iceType: pickIce(r),
    });
  }
  return rows
    .filter((c) =>
      filters.userId
        ? c.participants.some((p) => p.userId === filters.userId)
        : true,
    )
    .sort(
      (x, y) =>
        new Date(y.startedAt).getTime() - new Date(x.startedAt).getTime(),
    );
}

export class MockCallStatsDataSource implements CallStatsDataSource {
  async getSummary(filters: CallStatsFilters): Promise<SummaryResponse> {
    const calls = genCalls(filters, 'summary');
    const video = calls.filter((c) => c.type === 'video').length;
    const sip = calls.length - video;
    const failed = calls.filter((c) => c.status !== 'completed').length;
    const totalDur = calls.reduce((s, c) => s + c.durationSec, 0);
    const byStatus = calls.reduce(
      (acc, c) => {
        acc[c.status] += 1;
        return acc;
      },
      {
        completed: 0,
        rejected: 0,
        no_answer: 0,
        disconnected: 0,
        failed: 0,
      } as Record<CallStatus, number>,
    );
    const hours = windowHours(filters);
    const buckets = Math.min(
      48,
      Math.max(6, hours <= 6 ? hours * 2 : hours <= 24 ? 24 : 28),
    );
    const now = filters.to ? new Date(filters.to).getTime() : Date.now();
    const stepMs = (hours * 3_600_000) / buckets;
    const timeseries: TimeBucket[] = Array.from({ length: buckets }, (_, i) => {
      const start = now - (buckets - i) * stepMs;
      const inB = calls.filter((c) => {
        const t = new Date(c.startedAt).getTime();
        return t >= start && t < start + stepMs;
      });
      return {
        bucketStart: new Date(start).toISOString(),
        total: inB.length,
        video: inB.filter((c) => c.type === 'video').length,
        sip: inB.filter((c) => c.type === 'sip').length,
        failed: inB.filter((c) => c.status !== 'completed').length,
      };
    });
    return {
      totalCalls: calls.length,
      videoCalls: video,
      sipCalls: sip,
      failedCalls: failed,
      avgDurationSec: calls.length ? Math.round(totalDur / calls.length) : 0,
      totalDurationSec: totalDur,
      byType: { video, sip },
      byStatus,
      timeseries,
    };
  }

  async getByUser(
    filters: CallStatsFilters,
    limit = 10,
  ): Promise<ByUserResponse> {
    const calls = genCalls(filters, 'byUser');
    const map = new Map<string, UserAgg>();
    calls.forEach((c) =>
      c.participants.forEach((p) => {
        const u = map.get(p.userId) ?? {
          userId: p.userId,
          displayName: p.displayName,
          totalCalls: 0,
          videoCalls: 0,
          sipCalls: 0,
          failedCalls: 0,
          dur: 0,
        };
        u.totalCalls += 1;
        if (c.type === 'video') u.videoCalls += 1;
        else u.sipCalls += 1;
        if (c.status !== 'completed') u.failedCalls += 1;
        u.dur += c.durationSec;
        map.set(p.userId, u);
      }),
    );
    const users = [...map.values()]
      .sort((a, b) => b.totalCalls - a.totalCalls)
      .slice(0, limit)
      .map((u) => ({
        userId: u.userId,
        displayName: u.displayName,
        totalCalls: u.totalCalls,
        videoCalls: u.videoCalls,
        sipCalls: u.sipCalls,
        failedCalls: u.failedCalls,
        avgDurationSec: u.totalCalls ? Math.round(u.dur / u.totalCalls) : 0,
      }));
    return { users };
  }

  async getPair(filters: CallStatsFilters): Promise<PairSummary> {
    const a = ROSTER.find((u) => u.userId === filters.userId) ?? ROSTER[0];
    const b = ROSTER.find((u) => u.userId === filters.peerUserId) ?? ROSTER[1];
    const calls = genCalls(
      { ...filters, userId: undefined, peerUserId: undefined },
      'pair',
    )
      .map((c) => ({ ...c, participants: [a, b] }))
      .slice(0, 12);
    const video = calls.filter((c) => c.type === 'video').length;
    return {
      userA: a,
      userB: b,
      totalCalls: calls.length,
      video,
      sip: calls.length - video,
      failed: calls.filter((c) => c.status !== 'completed').length,
      avgDurationSec: calls.length
        ? Math.round(
            calls.reduce((s, c) => s + c.durationSec, 0) / calls.length,
          )
        : 0,
      calls,
    };
  }

  async getFailures(filters: CallStatsFilters): Promise<FailuresResponse> {
    const calls = genCalls(filters, 'failures');
    const failed = calls.filter((c) => c.status !== 'completed');
    const reasonOf = (s: CallStatus): FailureReason =>
      s === 'failed' ? 'setup_failed' : (s as FailureReason);
    const byReason = failed.reduce(
      (acc, c) => {
        acc[reasonOf(c.status)] += 1;
        return acc;
      },
      {
        no_answer: 0,
        rejected: 0,
        disconnected: 0,
        setup_failed: 0,
        unknown: 0,
      } as Record<FailureReason, number>,
    );
    const perUser = new Map<
      string,
      { p: Participant; failed: number; total: number }
    >();
    calls.forEach((c) =>
      c.participants.forEach((p) => {
        const e = perUser.get(p.userId) ?? { p, failed: 0, total: 0 };
        e.total += 1;
        if (c.status !== 'completed') e.failed += 1;
        perUser.set(p.userId, e);
      }),
    );
    const rankedUsers = [...perUser.values()]
      .filter((e) => e.failed > 0)
      .sort((a, b) => b.failed - a.failed)
      .slice(0, 10)
      .map((e) => ({
        userId: e.p.userId,
        displayName: e.p.displayName,
        failedCalls: e.failed,
        totalCalls: e.total,
        failureRatePct: round((e.failed / e.total) * 100, 1),
      }));
    return { totalFailed: failed.length, byReason, rankedUsers };
  }

  async getConnectivity(
    filters: CallStatsFilters,
  ): Promise<ConnectivityResponse> {
    const calls = genCalls(filters, 'connectivity');
    const ice = { host: 0, srflx_stun: 0, relay_turn: 0 };
    calls.forEach((c) => {
      if (c.iceType) ice[c.iceType] += 1;
    });
    const total = Math.max(1, ice.host + ice.srflx_stun + ice.relay_turn);
    return {
      ice,
      hostPct: round((ice.host / total) * 100, 1),
      stunPct: round((ice.srflx_stun / total) * 100, 1),
      turnPct: round((ice.relay_turn / total) * 100, 1),
    };
  }

  async getScores(filters: CallStatsFilters): Promise<Scores> {
    const calls = genCalls(filters, 'scores').filter(
      (c) => c.status === 'completed',
    );
    if (!calls.length)
      return {
        experience: 0,
        video: 0,
        audio: 0,
        mos: 0,
        connectivity: 0,
        callsScored: 0,
      };
    const r = mulberry32(seedFor(filters, 'scoreAgg'));
    const avg = (fn: (c: CallLogRow) => number) =>
      round(calls.reduce((s, c) => s + fn(c), 0) / calls.length);
    const experience = avg((c) => c.experienceScore ?? 0);
    const mosAvg = round(
      calls.reduce((s, c) => s + (c.mos ?? 0), 0) / calls.length,
      2,
    );
    return {
      experience,
      mos: mosAvg,
      audio: clamp(experience + round(r() * 8 - 4)),
      video: clamp(experience + round(r() * 10 - 6)),
      connectivity: clamp(experience + round(r() * 8 - 3)),
      callsScored: calls.length,
    };
  }

  async getByDimension(
    filters: CallStatsFilters,
    dimension: QualityDimension,
  ): Promise<ByDimensionResponse> {
    const calls = genCalls(filters, `dim-${dimension}`).filter(
      (c) => c.status === 'completed',
    );
    const groups = new Map<string, { label: string; rows: CallLogRow[] }>();
    const keyFor = (c: CallLogRow): { key: string; label: string } => {
      if (dimension === 'user') {
        const p = c.participants[0];
        return { key: p.userId, label: p.displayName };
      }
      if (dimension === 'participants') {
        const n = c.participants.length;
        return { key: `${n}`, label: `${n} participant${n > 1 ? 's' : ''}` };
      }
      const m = Math.floor(c.durationSec / 60);
      const key = m < 1 ? '0' : m < 5 ? '1' : m < 15 ? '5' : '15';
      const label =
        m < 1 ? '<1 min' : m < 5 ? '1–5 min' : m < 15 ? '5–15 min' : '15+ min';
      return { key, label };
    };
    calls.forEach((c) => {
      const { key, label } = keyFor(c);
      const g = groups.get(key) ?? { label, rows: [] };
      g.rows.push(c);
      groups.set(key, g);
    });
    const buckets: DimensionBucket[] = [...groups.entries()]
      .map(([key, g]) => {
        const avg = (fn: (c: CallLogRow) => number) =>
          round(g.rows.reduce((s, c) => s + fn(c), 0) / g.rows.length);
        return {
          key,
          label: g.label,
          calls: g.rows.length,
          experience: avg((c) => c.experienceScore ?? 0),
          audio: clamp(avg((c) => c.experienceScore ?? 0) + 2),
          video: clamp(avg((c) => c.experienceScore ?? 0) - 3),
          connectivity: clamp(avg((c) => c.experienceScore ?? 0) + 1),
          mos: round(
            g.rows.reduce((s, c) => s + (c.mos ?? 0), 0) / g.rows.length,
            2,
          ),
        };
      })
      .sort((a, b) => b.calls - a.calls);
    return { dimension, buckets };
  }

  async getCalls(query: CallsQuery): Promise<CallsPage> {
    let rows = genCalls(query, 'callsPage');
    if (query.type) rows = rows.filter((c) => c.type === query.type);
    if (query.status) rows = rows.filter((c) => c.status === query.status);
    const page = query.page ?? 0;
    const pageSize = query.pageSize ?? 25;
    return {
      total: rows.length,
      page,
      pageSize,
      rows: rows.slice(page * pageSize, page * pageSize + pageSize),
    };
  }

  async getCallDetail(callId: string): Promise<CallDetail> {
    const r = mulberry32(hash(callId));
    const type: CallType = r() < 0.68 ? 'video' : 'sip';
    const status: CallStatus = r() < 0.85 ? 'completed' : 'disconnected';
    const q = scoresFor(type, status, r);
    const durationSec = Math.round(60 + r() * 900);
    const a = ROSTER[Math.floor(r() * ROSTER.length)];
    const b = ROSTER[(ROSTER.indexOf(a) + 2) % ROSTER.length];
    const samples = Math.max(4, Math.round(durationSec / 30));
    const start = Date.now() - durationSec * 1000;
    const timeseries: MetricSample[] = Array.from(
      { length: samples },
      (_, i) => ({
        t: new Date(start + i * 30_000).toISOString(),
        rttMs: round(q.rtt + (r() * 40 - 20)),
        lossPct: round(Math.max(0, q.loss + (r() * 2 - 1)), 1),
        jitterMs: round(Math.max(0, q.jitter + (r() * 10 - 5)), 1),
        bitrateKbps: round(q.bitrate * (0.8 + r() * 0.4)),
        mos: round(Math.max(1, Math.min(5, q.mos + (r() * 0.4 - 0.2))), 2),
      }),
    );
    return {
      callId,
      startedAt: new Date(start).toISOString(),
      durationSec,
      type,
      status,
      participants: type === 'sip' ? [a] : [a, b],
      iceType: pickIce(r),
      scores: q.scores,
      metrics: {
        bitrateKbps: q.bitrate,
        rttMs: q.rtt,
        packetLossPct: q.loss,
        jitterMs: q.jitter,
        mos: q.mos,
      },
      timeseries,
    };
  }
}

interface UserAgg {
  userId: string;
  displayName: string;
  totalCalls: number;
  videoCalls: number;
  sipCalls: number;
  failedCalls: number;
  dur: number;
}
